<template>
  <div class="formulario-modelo">
    <h1>Auditoría de Evaluaciones</h1>
    <div class="divider"></div>

    <div class="info-bar">
      <span class="badge">Versión #{{ idVersion }}</span>
    </div>

    <!-- Botones de exportación -->
    <div class="export-bar">
      <button class="btn guardar" @click="exportar('json')" :disabled="exportando">
        Exportar JSON
      </button>
      <button class="btn guardar" @click="exportar('csv')" :disabled="exportando">
        Exportar CSV
      </button>
    </div>

    <!-- Filtros -->
    <div class="filtros-bar">
      <div class="filtro-grupo">
        <label>Usuario:</label>
        <input v-model.number="filtroUsuario" type="number" min="1" class="form-control form-sm" placeholder="ID" />
      </div>
      <div class="filtro-grupo">
        <label>Indicador:</label>
        <input v-model.number="filtroIndicador" type="number" min="1" class="form-control form-sm" placeholder="ID" />
      </div>
      <div class="filtro-grupo">
        <label>Acción:</label>
        <input v-model="filtroAccion" class="form-control form-sm" placeholder="Ej: creacion" />
      </div>
      <div class="filtro-grupo">
        <label>Desde:</label>
        <input v-model="filtroFechaDesde" type="date" class="form-control form-sm" />
      </div>
      <div class="filtro-grupo">
        <label>Hasta:</label>
        <input v-model="filtroFechaHasta" type="date" class="form-control form-sm" />
      </div>
      <button class="btn btn-sm guardar" @click="limpiarFiltros">Limpiar</button>
    </div>

    <div v-if="loading" class="info">Cargando registros de auditoría...</div>

    <!-- Tabla de auditoría -->
    <div v-else class="contenedor-tabla">
      <table v-if="registrosFiltrados.length" class="lista-tabla">
        <thead>
          <tr>
            <th>ID</th>
            <th>Evaluación</th>
            <th>Indicador</th>
            <th>Usuario</th>
            <th>Acción</th>
            <th>Detalle</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in registrosFiltrados" :key="r.id">
            <td>{{ r.id }}</td>
            <td>{{ r.id_evaluacion ?? '-' }}</td>
            <td>{{ r.id_indicador ?? '-' }}</td>
            <td>{{ r.id_usuario ?? '-' }}</td>
            <td><span class="badge-accion">{{ r.accion ?? '-' }}</span></td>
            <td class="detalle-cell">{{ formatDetalle(r.detalle) }}</td>
            <td>{{ formatFecha(r.fecha ?? r.created_at) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="mensaje-vacio">No hay registros de auditoría que coincidan con los filtros.</p>
    </div>

    <div class="botones">
      <button class="btn salir" @click="$router.back()">Volver</button>
    </div>

    <MensajeToast ref="toastRef" />
  </div>
</template>

<script setup lang="ts">
/**
 * Auditoría de las calificaciones de una versión.
 *
 * Solo lectura y exportación: la bitácora no se edita ni se borra, porque un
 * registro modificable no sirve como evidencia.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/version/:id_version/auditoria` (AuditoriaEvaluaciones) — permiso `calificacion.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useGestionAuditoria } from '@/store/GestionAuditoria'
import MensajeToast from '@/components/MensajeToast.vue'

const route = useRoute()
const idVersion = Number(route.params.id_version)
const auditoriaStore = useGestionAuditoria()
const toastRef = ref<InstanceType<typeof MensajeToast> | null>(null)

const loading = ref(false)
const exportando = ref(false)

const filtroUsuario = ref<number | null>(null)
const filtroIndicador = ref<number | null>(null)
const filtroAccion = ref('')
const filtroFechaDesde = ref('')
const filtroFechaHasta = ref('')

const registrosFiltrados = computed(() => {
  return auditoriaStore.registros.filter(r => {
    if (filtroUsuario.value && r.id_usuario !== filtroUsuario.value) return false
    if (filtroIndicador.value && r.id_indicador !== filtroIndicador.value) return false
    if (filtroAccion.value && !(r.accion ?? '').toLowerCase().includes(filtroAccion.value.toLowerCase())) return false
    if (filtroFechaDesde.value) {
      const fecha = r.fecha ?? r.created_at
      if (!fecha || fecha < filtroFechaDesde.value) return false
    }
    if (filtroFechaHasta.value) {
      const fecha = r.fecha ?? r.created_at
      if (!fecha || fecha.substring(0, 10) > filtroFechaHasta.value) return false
    }
    return true
  })
})

function limpiarFiltros() {
  filtroUsuario.value = null
  filtroIndicador.value = null
  filtroAccion.value = ''
  filtroFechaDesde.value = ''
  filtroFechaHasta.value = ''
}

function showToast(msg: string, tipo: 'exito' | 'error' = 'exito') {
  toastRef.value?.showToast(msg, tipo)
}

function formatDetalle(detalle: unknown): string {
  if (!detalle) return '-'
  if (typeof detalle === 'string') return detalle.length > 100 ? detalle.substring(0, 100) + '...' : detalle
  return JSON.stringify(detalle).substring(0, 100) + '...'
}

function formatFecha(fecha?: string | null): string {
  if (!fecha) return '-'
  const d = new Date(fecha)
  return isNaN(d.getTime()) ? fecha : d.toLocaleDateString('es-CO', {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
  })
}

async function exportar(formato: 'json' | 'csv') {
  exportando.value = true
  try {
    await auditoriaStore.exportar(idVersion, formato)
    showToast(`Exportación ${formato.toUpperCase()} descargada`)
  } catch {
    showToast('Error al exportar', 'error')
  } finally {
    exportando.value = false
  }
}

onMounted(async () => {
  if (!Number.isFinite(idVersion) || idVersion <= 0) return
  loading.value = true
  try {
    await auditoriaStore.cargarPorVersion(idVersion)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

h1 { font-size: 1.4rem; font-weight: 700; margin: 0; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }
.info-bar { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
.badge { display: inline-block; padding: 4px 10px; border-radius: 4px; background-color: #e8f4fd; color: #0e69a0; font-weight: 600; font-size: 0.85rem; }
.info { padding: 1rem 0; color: #555; }
.mensaje-vacio { color: #888; font-style: italic; padding: 1rem 0; }

.export-bar { display: flex; gap: 0.75rem; margin-bottom: 1rem; }

.filtros-bar {
  display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: flex-end;
  margin-bottom: 1.5rem; padding: 1rem; background: #f8f9fa; border-radius: 8px;
}
.filtro-grupo { display: flex; flex-direction: column; gap: 0.25rem; }
.filtro-grupo > label { font-size: 0.75rem; font-weight: 600; color: #555; }
.form-control {
  padding: 0.4rem 0.6rem; border: 1px solid #ddd; border-radius: 6px;
  font-size: 0.85rem; font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.form-control:focus { outline: none; border-color: #0e69a0; box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1); }
.form-sm { max-width: 130px; }

.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; min-width: 800px; }
.lista-tabla th, .lista-tabla td {
  padding: 10px 12px; text-align: left; border-bottom: 1px solid #e0e0e0; font-size: 0.85rem;
}
.lista-tabla thead { background-color: #f5f5f5; font-weight: 600; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.05em; }

.badge-accion {
  display: inline-block; padding: 2px 6px; border-radius: 4px;
  background: #e8f4fd; color: #0e69a0; font-size: 0.75rem; font-weight: 600;
}

.detalle-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.8rem; color: #666; }

.botones { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; }

.btn {
  padding: 0.6rem 1.25rem; font-weight: 600; border: none; border-radius: 8px;
  cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.9rem;
}
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:hover { background-color: #0e69a0; color: white; }
.btn.guardar:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
